import { useState, useEffect, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useProducts } from '../../hooks/useProducts';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import BidPanel from './components/BidPanel';
import ReportModal from './components/ReportModal';
import QRGenerator from '../dashboard/components/QRGenerator';

// Componente seguro para el mapa — evita que un error del iframe rompa la página
interface MapaUbicacionProps {
  latitud?: number | null;
  longitud?: number | null;
  direccion?: string | null;
  pueblo?: string | null;
}

class MapaUbicacion extends Component<MapaUbicacionProps, { hasError: boolean }> {
  constructor(props: MapaUbicacionProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-72 rounded-xl bg-gray-100 flex items-center justify-center border border-gray-200">
          <div className="text-center text-gray-500">
            <i className="ri-map-pin-line text-3xl mb-2 block"></i>
            <p className="text-sm">Mapa no disponible</p>
          </div>
        </div>
      );
    }

    const { latitud, longitud, direccion, pueblo } = this.props;
    const puebloSeguro = pueblo || 'San Juan';

    let mapSrc = '';
    if (latitud && longitud) {
      mapSrc = `https://maps.google.com/maps?q=${latitud},${longitud}&z=15&output=embed`;
    } else if (direccion) {
      mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(direccion + ', Puerto Rico')}&z=14&output=embed`;
    } else {
      mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(puebloSeguro + ', Puerto Rico')}&z=13&output=embed`;
    }

    const titulo = latitud && longitud ? 'Ubicación de la Propiedad' : direccion ? 'Ubicación de la Propiedad' : 'Ubicación General';

    return (
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <i className="ri-map-pin-2-line text-emerald-600"></i>
          {titulo}
        </h3>
        {direccion && (
          <p className="text-sm text-gray-600 mb-3 flex items-center gap-2">
            <i className="ri-map-pin-line text-gray-400"></i>
            {direccion}
          </p>
        )}
        <div className="w-full h-72 rounded-xl overflow-hidden border border-gray-200">
          <iframe
            title={titulo}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            src={mapSrc}
          />
        </div>
      </div>
    );
  }
}

const ProductoPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { productos } = useProducts();
  const [producto, setProducto] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [cantidad, setCantidad] = useState(1);
  const [tipoEnvio, setTipoEnvio] = useState<'regular' | 'pesada'>('regular');
  const [showContactModal, setShowContactModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [contactForm, setContactForm] = useState({ nombre: '', telefono: '', mensaje: '' });
  const [procesandoReserva, setProcesandoReserva] = useState(false);
  const [procesandoCompra, setProcesandoCompra] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [vendedorTelefono, setVendedorTelefono] = useState<string | null>(null);
  const [vendedorInfo, setVendedorInfo] = useState<any>(null);
  const [errorVendedor, setErrorVendedor] = useState<string | null>(null);
  const [repData, setRepData] = useState<any>(null);
  const [showRepLinkModal, setShowRepLinkModal] = useState(false);
  const [repLinkCopied, setRepLinkCopied] = useState(false);

  useEffect(() => {
    if (user) {
      supabase
        .from('representatives')
        .select('rep_code, estado')
        .eq('user_id', user.id)
        .eq('estado', 'activo')
        .maybeSingle()
        .then(({ data }) => setRepData(data));
    }
  }, [user]);

  useEffect(() => {
    const fetchProducto = async () => {
      if (!id) return;
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
      if (!error && data) {
        setProducto(data);
        // Cargar información del vendedor
        if (data.vendedor_id) {
          const { data: perfil, error: perfilError } = await supabase
            .from('profiles')
            .select('id, nombre_completo, telefono, stripe_connect_id, stripe_connect_status')
            .eq('user_id', data.vendedor_id)
            .maybeSingle();
          
          if (perfilError) {
            console.error('Error cargando perfil del vendedor:', perfilError);
            setErrorVendedor('Error al verificar el vendedor');
          } else if (!perfil) {
            console.error('Vendedor no encontrado para producto:', data.id);
            setErrorVendedor('Vendedor no verificado - No se puede procesar la compra');
          } else {
            setVendedorInfo(perfil);
            if (perfil?.telefono) setVendedorTelefono(perfil.telefono);
          }
        } else {
          console.error('Producto sin vendedor_id:', data.id);
          setErrorVendedor('Producto sin vendedor asignado - No disponible para compra');
        }
      }
      setLoading(false);
    };
    fetchProducto();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!producto) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Producto no encontrado</h2>
          <Link to="/catalogo" className="text-gray-700 hover:underline">Volver al catálogo</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const esAuto = producto?.categoria === 'autos';
  const esBienesRaices = producto?.categoria === 'bienes_raices';
  const esSubasta = producto?.tipo_venta === 'subasta';
  const estaReservado = producto?.estado_venta === 'reservado';
  const tieneVendedorValido = !!vendedorInfo && !errorVendedor;
  const vendedorTieneStripeConnect = vendedorInfo?.stripe_connect_status === 'active' && !!vendedorInfo?.stripe_connect_id;
  const envioNacional = producto?.envio_nacional === true;

  const precioSeguro = typeof producto?.precio === 'number' && !isNaN(producto.precio) ? producto.precio : 0;
  const subtotal = precioSeguro * cantidad;
  const ivu = Math.round(subtotal * 0.115 * 100) / 100;
  // El costo de envío USPS se calcula dinámicamente en Stripe con Shippo.
  // Solo Island Wide y Carga Pesada tienen tarifa fija local.
  const esEnvioUsps = envioNacional;
  const costoEnvioLocal = tipoEnvio === 'pesada' ? 45 : 15;
  const total = subtotal + ivu + (esEnvioUsps ? 0 : costoEnvioLocal);

  const productosRelacionados = (Array.isArray(productos) ? productos : [])
    .filter(p => p?.categoria === producto?.categoria && p?.id !== producto?.id)
    .slice(0, 4);

  const waNumber = vendedorTelefono
    ? `1${vendedorTelefono.replace(/\D/g, '')}`
    : '17871234567';
  const whatsappMessage = encodeURIComponent(
    `Hola, estoy interesado en "${producto?.titulo || 'este artículo'}" publicado en IslaVende por $${precioSeguro.toLocaleString()}. ¿Está disponible?`
  );
  const whatsappUrl = `https://wa.me/${waNumber}?text=${whatsappMessage}`;

  const handleReservarAuto = async () => {
    if (!user) {
      setShowContactModal(true);
      return;
    }

    setProcesandoReserva(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-auto-reservation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            productoId: producto.id,
            vendedorId: producto.vendedor_id,
            compradorId: user.id,
            tituloAuto: producto.titulo,
            precioAuto: producto.precio,
          }),
        }
      );

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No se pudo crear la sesión de pago');
      }
    } catch (error) {
      console.error('Error al reservar auto:', error);
      alert('Hubo un error al procesar tu reserva. Por favor intenta de nuevo.');
    } finally {
      setProcesandoReserva(false);
    }
  };

  const handleComprarAhora = async () => {
    if (!user) {
      setShowContactModal(true);
      return;
    }

    if (!tieneVendedorValido) {
      alert('Este producto no tiene un vendedor verificado. No se puede procesar la compra.');
      return;
    }

    if (!vendedorTieneStripeConnect) {
      alert('El vendedor aún no ha configurado su cuenta de cobro. No se puede procesar la compra en este momento.');
      return;
    }

    setProcesandoCompra(true);
    try {
      // SIEMPRE forzar refresh del token antes de llamar al checkout
      // Esto garantiza que el token sea fresco sin importar cuánto tiempo lleva la sesión
      const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError || !refreshed.session) {
        // Si no se puede refrescar, intentar obtener la sesión actual
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !sessionData.session) {
          console.error('No hay sesión activa:', sessionError?.message);
          setShowContactModal(true);
          setProcesandoCompra(false);
          return;
        }
        // Usar el token actual si el refresh falló pero hay sesión
        console.warn('Refresh falló, usando token actual:', refreshError?.message);
      }

      // Obtener el token más fresco disponible
      const { data: finalSession } = await supabase.auth.getSession();
      const accessToken = finalSession.session?.access_token;

      if (!accessToken) {
        console.error('No se pudo obtener access token');
        setShowContactModal(true);
        setProcesandoCompra(false);
        return;
      }

      const { data: perfil } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      const compradorId = perfil?.id;
      if (!compradorId) {
        setShowContactModal(true);
        setProcesandoCompra(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify({
            productoId: producto.id,
            vendedorId: vendedorInfo.id,
            compradorId,
            tituloProducto: producto.titulo,
            precioProducto: producto.precio,
            tipoEnvio,
            cantidad,
            representanteId: localStorage.getItem('ref') || '',
            envioNacional: producto.envio_nacional === true,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error('Error del servidor:', response.status, data);
        if (data.code === 'SESSION_EXPIRED' || data.code === 'NO_TOKEN') {
          await supabase.auth.signOut();
          setShowContactModal(true);
          return;
        } else if (data.code === 'VENDOR_NOT_FOUND' || data.code === 'MISSING_VENDOR') {
          throw new Error('Vendedor no encontrado. Este producto no está disponible para compra.');
        } else if (data.code === 'SELLER_NO_STRIPE_CONNECT') {
          throw new Error(`El vendedor no tiene configurada su cuenta de cobro: ${data.vendedor_name || ''}`);
        } else {
          throw new Error(data.error || `Error ${response.status} al procesar el pago`);
        }
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'No se pudo iniciar el pago');
      }
    } catch (error: any) {
      console.error('Error al iniciar compra:', error);
      alert(error.message || 'Hubo un error al procesar tu pago. Por favor intenta de nuevo.');
    } finally {
      setProcesandoCompra(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
            <Link to="/" className="hover:text-gray-900 transition-colors">Inicio</Link>
            <i className="ri-arrow-right-s-line"></i>
            {esBienesRaices ? (
              <Link to="/bienes-raices" className="hover:text-gray-900 transition-colors">Bienes Raíces</Link>
            ) : esSubasta ? (
              <Link to="/subastas" className="hover:text-gray-900 transition-colors">Subastas</Link>
            ) : (
              <Link to="/catalogo" className="hover:text-gray-900 transition-colors">Catálogo</Link>
            )}
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium">{producto.titulo}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Galería */}
            <div>
              <div className="relative w-full h-96 lg:h-[520px] bg-white rounded-2xl overflow-hidden mb-4">
                {estaReservado && (
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-10 flex items-center justify-center">
                    <div className="bg-white rounded-2xl px-8 py-6 text-center">
                      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-bookmark-fill text-3xl text-orange-500"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">RESERVADO</h3>
                      <p className="text-sm text-gray-600">Este vehículo ya tiene una reserva activa</p>
                    </div>
                  </div>
                )}
                <img
                  src={producto.fotos?.[0] || 'https://public.readdy.ai/ai/img_res/c8a6a02d4f593e0161d11bc82d8f64a4.jpg'}
                  alt={producto.titulo}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {esSubasta && (
                    <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-auction-line"></i> SUBASTA
                    </span>
                  )}
                  {esBienesRaices && producto.tipo_propiedad && (
                    <span className="bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-building-2-line"></i> {producto.tipo_propiedad}
                    </span>
                  )}
                  {esAuto && (
                    <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-car-line"></i> Vehículo
                    </span>
                  )}
                </div>
                <span className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold text-gray-700">
                  {producto.estado}
                </span>
              </div>

              {/* Fotos adicionales */}
              {producto.fotos && producto.fotos.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {producto.fotos.slice(1).map((foto: string, i: number) => (
                    <div key={i} className="flex-shrink-0 w-24 h-20 rounded-xl overflow-hidden">
                      <img src={foto} alt={`Foto ${i + 2}`} className="w-full h-full object-cover object-top" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Panel derecho */}
            <div className="space-y-5">
              <div className="bg-white rounded-2xl p-7">
                <h1 className="text-2xl font-bold text-gray-900 mb-3">{producto.titulo}</h1>
                <div className="flex items-center gap-4 mb-5 pb-5 border-b border-gray-100">
                  <div className="flex items-center text-gray-500 text-sm">
                    <i className="ri-map-pin-line mr-1"></i>
                    {producto.pueblo}
                  </div>
                  {esBienesRaices && producto.tipo_propiedad && (
                    <span className="text-sm text-gray-500">{producto.tipo_propiedad}</span>
                  )}
                  {/* Etiqueta de cobertura de envío */}
                  {!esAuto && !esBienesRaices && (
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${
                      envioNacional
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}>
                      <i className={envioNacional ? 'ri-flight-takeoff-line' : 'ri-map-pin-2-line'}></i>
                      {envioNacional ? 'Envío a todo EE. UU.' : 'Entrega: Local (Puerto Rico)'}
                    </span>
                  )}
                </div>

                {/* Alerta de vendedor no verificado */}
                {errorVendedor && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 flex gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-red-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-red-700 font-semibold leading-relaxed">
                        {errorVendedor}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Este producto no está disponible para compra en este momento.
                      </p>
                    </div>
                  </div>
                )}

                {/* Alerta de vendedor sin Stripe Connect */}
                {tieneVendedorValido && !vendedorTieneStripeConnect && !esAuto && !esBienesRaices && !esSubasta && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-time-line text-amber-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-amber-700 font-semibold leading-relaxed">
                        Vendedor en proceso de verificación
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        El vendedor aún no ha completado su configuración de cobro. La compra estará disponible pronto.
                      </p>
                    </div>
                  </div>
                )}

                {/* Precio (solo si no es subasta) */}
                {!esSubasta && (
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">
                      ${precioSeguro.toLocaleString()}
                    </span>
                  </div>
                )}

                {/* ── SUBASTA ── */}
                {esSubasta ? (
                  <BidPanel producto={producto} />
                ) : esAuto ? (
                  <>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-5 flex gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-orange-500 text-lg"></i>
                      </div>
                      <p className="text-sm text-orange-700 leading-relaxed">
                        Para vehículos, el pago y financiamiento se coordina directamente con el vendedor o dealer. Sin comisiones de plataforma.
                      </p>
                    </div>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                      className={`flex items-center justify-center gap-3 w-full py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-colors text-base mb-3 whitespace-nowrap ${estaReservado ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                      <div className="w-6 h-6 flex items-center justify-center"><i className="ri-whatsapp-line text-2xl"></i></div>
                      Contactar al Vendedor por WhatsApp
                    </a>
                    <button onClick={() => setShowWhatsAppModal(true)} disabled={estaReservado}
                      className={`flex items-center justify-center gap-2 w-full py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-colors text-base mb-4 whitespace-nowrap ${estaReservado ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-send-line text-lg"></i></div>
                      Enviar Mensaje al Vendedor
                    </button>
                    {!estaReservado && (
                      <button onClick={() => setShowContactModal(true)}
                        className="flex items-center justify-center gap-3 w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold rounded-xl transition-all text-base whitespace-nowrap cursor-pointer border-2 border-orange-400">
                        <div className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-line text-2xl"></i></div>
                        Llamar al Vendedor
                      </button>
                    )}
                  </>
                ) : esBienesRaices ? (
                  <>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5 flex gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-emerald-600 text-lg"></i>
                      </div>
                      <p className="text-sm text-emerald-700 leading-relaxed">
                        Para propiedades, el proceso de compra se coordina directamente con el vendedor o agente. Sin comisiones de plataforma.
                      </p>
                    </div>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 w-full py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-colors text-base mb-3 whitespace-nowrap cursor-pointer">
                      <div className="w-6 h-6 flex items-center justify-center"><i className="ri-whatsapp-line text-2xl"></i></div>
                      Contactar al Vendedor
                    </a>
                    <button onClick={() => setShowWhatsAppModal(true)}
                      className="flex items-center justify-center gap-2 w-full py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-colors text-base mb-4 whitespace-nowrap cursor-pointer">
                      <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-send-line text-lg"></i></div>
                      Enviar Mensaje al Vendedor
                    </button>
                    <button onClick={() => setShowContactModal(true)}
                      className="flex items-center justify-center gap-3 w-full py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold rounded-xl transition-all text-base whitespace-nowrap cursor-pointer border-2 border-emerald-400">
                      <div className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-line text-2xl"></i></div>
                      Llamar al Agente
                    </button>
                  </>
                ) : (
                  <>
                    <div className="mb-5">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Cantidad</label>
                      <div className="flex items-center space-x-3">
                        <button onClick={() => setCantidad(Math.max(1, cantidad - 1))}
                          className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
                          <i className="ri-subtract-line text-xl"></i>
                        </button>
                        <input type="number" value={cantidad} onChange={(e) => setCantidad(Math.max(1, Number(e.target.value)))}
                          className="w-20 text-center py-2 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-400" />
                        <button onClick={() => setCantidad(cantidad + 1)}
                          className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
                          <i className="ri-add-line text-xl"></i>
                        </button>
                      </div>
                    </div>
                    {/* Opciones de envío — solo para Island Wide (no USPS nacional) */}
                    {!envioNacional && (
                      <div className="mb-5">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Tipo de Envío Island Wide</label>
                        <div className="space-y-3">
                          {[
                            { val: 'regular', label: 'Envío Regular', desc: 'Entrega en 3-5 días', precio: 15 },
                            { val: 'pesada', label: 'Carga Pesada', desc: 'Para artículos grandes', precio: 45 },
                          ].map(opt => (
                            <label key={opt.val} className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                              <div className="flex items-center">
                                <input type="radio" name="envio" value={opt.val} checked={tipoEnvio === opt.val}
                                  onChange={() => setTipoEnvio(opt.val as 'regular' | 'pesada')} className="mr-3 cursor-pointer" />
                                <div>
                                  <div className="font-semibold text-gray-900 text-sm">{opt.label}</div>
                                  <div className="text-xs text-gray-500">{opt.desc}</div>
                                </div>
                              </div>
                              <span className="font-bold text-gray-900">${opt.precio}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Aviso de envío USPS dinámico */}
                    {envioNacional && (
                      <div className="mb-5 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-truck-line text-emerald-600 text-lg"></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-emerald-800 mb-0.5">Envío USPS calculado al finalizar</p>
                          <p className="text-xs text-emerald-700 leading-relaxed">
                            Verás las opciones de USPS Ground Advantage y Priority Mail con sus precios reales al ingresar tu dirección en el checkout de Stripe.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="bg-gray-50 rounded-xl p-4 mb-5">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Subtotal</span>
                        <span className="font-semibold text-gray-900">${isNaN(subtotal) ? '0.00' : subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">IVU (11.5%)</span>
                        <span className="font-semibold text-gray-900">${isNaN(ivu) ? '0.00' : ivu.toFixed(2)}</span>
                      </div>
                      {envioNacional ? (
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Envío USPS</span>
                          <span className="text-sm font-semibold text-emerald-700 flex items-center gap-1">
                            <i className="ri-information-line"></i>
                            Se calcula en checkout
                          </span>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Envío Island Wide</span>
                          <span className="font-semibold text-gray-900">${costoEnvioLocal}</span>
                        </div>
                      )}
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-gray-900">
                            {envioNacional ? 'Subtotal (sin envío)' : 'Total'}
                          </span>
                          <span className="text-2xl font-bold text-gray-900">
                            ${isNaN(total) ? '0.00' : total.toFixed(2)}
                            {envioNacional && <span className="text-sm font-normal text-gray-500 ml-1">+ envío</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button onClick={handleComprarAhora} disabled={procesandoCompra || !tieneVendedorValido || !vendedorTieneStripeConnect}
                      className="w-full py-4 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors text-base mb-3 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {procesandoCompra ? (
                        <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Procesando...</>
                      ) : !tieneVendedorValido ? (
                        <><i className="ri-error-warning-line text-lg"></i>Vendedor no verificado</>
                      ) : !vendedorTieneStripeConnect ? (
                        <><i className="ri-time-line text-lg"></i>Vendedor en verificación</>
                      ) : envioNacional ? (
                        <><i className="ri-secure-payment-line text-lg"></i>Comprar Ahora — Elegir envío en checkout</>
                      ) : (
                        <><i className="ri-secure-payment-line text-lg"></i>Comprar Ahora — ${isNaN(total) ? '0.00' : total.toFixed(2)}</>
                      )}
                    </button>
                    <p className="text-xs text-center text-gray-500 flex items-center justify-center gap-1">
                      <i className="ri-shield-check-line text-green-500"></i>
                      Pago seguro con Stripe · Tarjetas aceptadas
                    </p>
                  </>
                )}

                {/* Botón Representante */}
                {repData && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => setShowRepLinkModal(true)}
                      className="w-full py-3 border-2 border-amber-400 text-amber-700 font-semibold rounded-xl hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm"
                    >
                      <i className="ri-qr-code-line text-lg"></i>
                      Generar mi Link de Venta
                    </button>
                    <p className="text-xs text-center text-gray-400 mt-1.5">
                      Comparte y gana {repData.comision_porcentaje || 20}% de comisión
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Descripción */}
          <div className="bg-white rounded-2xl p-8 mb-8">
            <div className="flex items-start justify-between mb-5">
              <h2 className="text-2xl font-bold text-gray-900">Descripción</h2>
              <button
                onClick={() => setShowReportModal(true)}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flag-line"></i>Reportar anuncio
              </button>
            </div>
            <p className="text-gray-700 text-base leading-relaxed">
              {producto.descripcion || `Este ${producto.titulo} está disponible en IslaVende. Contacta al vendedor para más información.`}
            </p>
          </div>

          {/* Especificaciones Bienes Raíces */}
          {esBienesRaices && (
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl">
                  <i className="ri-building-2-line text-2xl text-emerald-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Especificaciones de la Propiedad</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { show: producto.habitaciones != null, icon: 'ri-hotel-bed-line', label: 'Habitaciones', value: producto.habitaciones },
                  { show: producto.banos != null, icon: 'ri-drop-line', label: 'Baños', value: producto.banos },
                  { show: producto.estacionamientos != null, icon: 'ri-car-line', label: 'Estacionamientos', value: producto.estacionamientos },
                  { show: producto.pies_cuadrados != null, icon: 'ri-aspect-ratio-line', label: 'Pies Cuadrados', value: `${(producto.pies_cuadrados ?? 0).toLocaleString()} ft²` },
                  { show: producto.cuerdas != null, icon: 'ri-landscape-line', label: 'Cuerdas', value: producto.cuerdas },
                  { show: !!producto.tipo_propiedad, icon: 'ri-home-4-line', label: 'Tipo', value: producto.tipo_propiedad },
                  { show: !!producto.pueblo, icon: 'ri-map-pin-line', label: 'Pueblo', value: producto.pueblo },
                ].filter(s => s.show).map(spec => (
                  <div key={spec.label} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                      <i className={`${spec.icon} text-lg text-emerald-600`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">{spec.label}</p>
                      <p className="font-bold text-gray-900 text-sm">{spec.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mapa de ubicación */}
              <MapaUbicacion
                latitud={producto.latitud}
                longitud={producto.longitud}
                direccion={producto.direccion_propiedad}
                pueblo={producto.pueblo}
              />
            </div>
          )}

          {/* Especificaciones Moda */}
          {producto.categoria === 'moda' && (
            producto.talla || producto.color || producto.material || producto.marca
          ) && (
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-xl">
                  <i className="ri-shirt-line text-2xl text-gray-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Especificaciones del Artículo</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { show: !!producto.marca, icon: 'ri-price-tag-3-line', label: 'Marca', value: producto.marca },
                  { show: !!producto.talla && !producto.talla_calzado, icon: 'ri-ruler-2-line', label: 'Talla', value: producto.talla },
                  { show: !!producto.talla_calzado, icon: 'ri-footprint-line', label: 'Talla Calzado', value: `US ${producto.talla_calzado}` },
                  { show: !!producto.color, icon: 'ri-palette-line', label: 'Color', value: producto.color },
                  { show: !!producto.material, icon: 'ri-leaf-line', label: 'Material', value: producto.material },
                ].filter(s => s.show).map(spec => (
                  <div key={spec.label} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                      <i className={`${spec.icon} text-lg text-gray-600`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">{spec.label}</p>
                      <p className="font-bold text-gray-900 text-sm">{spec.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Productos relacionados */}
          {productosRelacionados.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Productos Relacionados</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
                {productosRelacionados.map((prod) => (
                  <Link key={prod.id} to={`/producto/${prod.id}`}
                    className="bg-white rounded-xl overflow-hidden border-2 border-gray-100 hover:border-gray-300 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer">
                    <div className="relative w-full h-56">
                      <img src={prod.imagen || prod.fotos?.[0]} alt={prod.titulo} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2 text-sm line-clamp-2">{prod.titulo}</h3>
                      <span className="text-xl font-bold text-gray-900">${prod.precio}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal login */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-4xl text-green-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Casi listo!</h3>
              <p className="text-gray-600 text-sm">
                Para continuar, necesitas iniciar sesión o crear una cuenta.
              </p>
            </div>
            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap text-center"
              >
                Iniciar Sesión
              </Link>
              <Link
                to="/registro"
                className="block w-full py-3 border-2 border-gray-900 text-gray-900 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap text-center"
              >
                Crear Cuenta
              </Link>
              <button
                onClick={() => setShowContactModal(false)}
                className="w-full py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rep Link + QR */}
      {showRepLinkModal && producto && repData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <i className="ri-qr-code-line text-amber-500"></i>
                Mi Link de Venta
              </h3>
              <button onClick={() => setShowRepLinkModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="flex flex-col items-center gap-4">
              <QRGenerator
                url={`https://islavende.com/producto/${producto.id}?rep=${repData.rep_code}`}
                size={180}
                downloadName={`qr-${producto.id}-${repData.rep_code}`}
              />
              <div className="w-full">
                <p className="text-xs text-gray-500 mb-1.5 font-semibold">Tu link personalizado:</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono text-gray-700 truncate">
                    {`https://islavende.com/producto/${producto.id}?rep=${repData.rep_code}`}
                  </div>
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(`https://islavende.com/producto/${producto.id}?rep=${repData.rep_code}`);
                      setRepLinkCopied(true);
                      setTimeout(() => setRepLinkCopied(false), 2000);
                    }}
                    className="px-3 py-2 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className={repLinkCopied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  </button>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 w-full">
                <p className="text-xs text-amber-700 flex items-start gap-2">
                  <i className="ri-lightbulb-line flex-shrink-0 mt-0.5"></i>
                  Descarga el QR e imprímelo o compártelo por WhatsApp. Cada compra usando este link te genera comisión automáticamente.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reporte */}
      {showReportModal && producto && (
        <ReportModal
          productoId={producto.id}
          productoTitulo={producto.titulo}
          onClose={() => setShowReportModal(false)}
        />
      )}

      {/* Modal WhatsApp */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Contactar al Vendedor</h3>
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-5 flex gap-2 items-start">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-car-line text-orange-500"></i>
              </div>
              <p className="text-xs text-orange-700 leading-relaxed">
                <strong>Para vehículos</strong>, el pago y financiamiento se coordina directamente con el vendedor o dealer.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Tu nombre</label>
                <input
                  type="text"
                  value={contactForm.nombre}
                  onChange={(e) => setContactForm({ ...contactForm, nombre: e.target.value })}
                  placeholder="Ej. Juan García"
                  className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Tu teléfono</label>
                <input
                  type="tel"
                  value={contactForm.telefono}
                  onChange={(e) => setContactForm({ ...contactForm, telefono: e.target.value })}
                  placeholder="(787) 000-0000"
                  className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Mensaje</label>
                <textarea
                  value={contactForm.mensaje}
                  onChange={(e) => setContactForm({ ...contactForm, mensaje: e.target.value })}
                  rows={3}
                  maxLength={500}
                  placeholder={`Hola, estoy interesado en ${producto?.titulo || 'este artículo'}...`}
                  className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 resize-none"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{contactForm.mensaje.length}/500</p>
              </div>
            </div>

            <a
              href={`https://wa.me/${waNumber}?text=${encodeURIComponent(
                `Hola, soy ${contactForm.nombre || 'un interesado'}. ${contactForm.mensaje || `Me interesa ${producto?.titulo || 'este artículo'} por $${precioSeguro.toLocaleString()}`}. Tel: ${contactForm.telefono || 'no indicado'}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-xl"></i>
              </div>
              Enviar por WhatsApp
            </a>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductoPage;
